var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext, useEffect, useState } from "react";
import { PaymentFormContext } from "../../../Shared/components/PaymentForm/PaymentFormContext";
import { useCurrencyLocalizer } from "../../../Shared/hooks/useCurrencyLocalizer";
import { useMediaQuery, useTheme } from "@mui/material";
import { getOpenBalancePageData } from "../utils/openBalancePage-requests";
import { useLocation } from "react-router-dom-v5-compat";
export const useOpenBalancePage = () => {
    var _a, _b, _c, _d, _e;
    const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"), { noSsr: true });
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const orderNumber = searchParams.get("orderNumber");
    // State variables
    const [isLoading, setIsLoading] = useState(true);
    const [openInvoice, setOpenInvoice] = useState(false);
    const [paymentComplete, setPaymentComplete] = useState(false);
    const [queryData, setQueryData] = useState(null);
    // Context
    const { balance: contextBalanceObject, setBalance: setContextBalanceObject } = useContext(PaymentFormContext);
    useEffect(() => {
        let mounted = true;
        if (mounted) {
            const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
                const response = yield getOpenBalancePageData(orderNumber);
                if (response) {
                    setQueryData(response.queryData);
                    setIsLoading(false);
                }
            });
            fetchData();
        }
        return () => {
            mounted = false;
        };
    }, [orderNumber]);
    // Validating that the order is found in the queryData (it should be the first in the array, but we are checking for accuracy against the orderId and orderNumber)
    const targetOrder = (_a = queryData === null || queryData === void 0 ? void 0 : queryData.targetOrder) !== null && _a !== void 0 ? _a : null;
    // The order's balance cannot be paid via the site if the order is past cutoff.
    const isOrderPastCutoff = (targetOrder === null || targetOrder === void 0 ? void 0 : targetOrder.AllMealDaysPastCutOff) || (targetOrder === null || targetOrder === void 0 ? void 0 : targetOrder.IsOrderPastCutOff);
    const targetOrderInfo = (_b = queryData === null || queryData === void 0 ? void 0 : queryData.orderInfo) !== null && _b !== void 0 ? _b : null;
    const balanceObject = (_c = targetOrderInfo === null || targetOrderInfo === void 0 ? void 0 : targetOrderInfo.Balance) !== null && _c !== void 0 ? _c : null;
    const remainingBalance = balanceObject === null || balanceObject === void 0 ? void 0 : balanceObject.remainingBalance;
    const hasBalance = !!remainingBalance && remainingBalance > 0;
    const remainingBalanceDisplay = useCurrencyLocalizer(remainingBalance !== null && remainingBalance !== void 0 ? remainingBalance : 0, false).localizedCurrencyText;
    const contextBalanceObjectRemainingBalance = (_d = contextBalanceObject === null || contextBalanceObject === void 0 ? void 0 : contextBalanceObject.remainingBalance) !== null && _d !== void 0 ? _d : 0;
    // The order has already been paid if the order exists, it does not have a remaining balance,
    // and the payment was not completed on this page.
    const isAlreadyPaid = !!targetOrder && (remainingBalance !== null && remainingBalance !== void 0 ? remainingBalance : 0) === 0 && !paymentComplete;
    const shouldPromptBeforeNavigation = !paymentComplete && !!remainingBalance && !!targetOrder;
    const isOrderValidForPayment = !!targetOrder && !isOrderPastCutoff && hasBalance;
    const pastCutoffMarkdown = (_e = queryData === null || queryData === void 0 ? void 0 : queryData.openBalanceCutoffMessage) !== null && _e !== void 0 ? _e : null;
    const showOrderInvoice = !!targetOrder && !!targetOrderInfo && openInvoice;
    useEffect(() => {
        if (hasBalance && contextBalanceObjectRemainingBalance !== remainingBalance) {
            setContextBalanceObject(balanceObject);
        }
    }, [hasBalance, contextBalanceObjectRemainingBalance, remainingBalance]);
    return {
        isAlreadyPaid,
        isLoading,
        isMobile,
        isOrderPastCutoff,
        isOrderValidForPayment,
        pastCutoffMarkdown,
        paymentComplete,
        remainingBalanceDisplay,
        setIsLoading,
        setOpenInvoice,
        setPaymentComplete,
        shouldPromptBeforeNavigation,
        showOrderInvoice,
        targetOrder,
        targetOrderInfo,
    };
};
